<template>
  <div
    v-if="menuList && menuList.length > 0"
    class="ui-button-multi"
  >
    <ui-button
      v-if="firstItem"
      :class="{
        'ui-button-multi__first-item': true,
        'ui-button-multi__first-item--has-rest': !ignoreRest,
        'ui-button-multi__first-item--ignore-rest': ignoreRest,
      }"
      key="first"
      :icon="icon || firstItem.icon"
      :text="displayText ? firstItem.title : undefined"
      :disabled="firstItem.disabled"
      :button-style="buttonStyle"
      :outline="outline"
      :size="size"
      :plan-limits="firstItem.planLimits"
      @click="mainButtonClickHandle"
    />

    <ui-button
      v-if="!ignoreRest"
      :class="{
        'ui-button-multi__rest-items': true,
        'ui-button-multi__rest-items--has-border': hasBorder,
        'ui-button-multi__rest-items--no-border': !hasBorder,
      }"
      key="select"
      :has-menu="true"
      :menu-list="restItems"
      :button-style="!isOutline ? buttonStyle : 'default'"
      :outline="outline"
      :size="size"
      :force-chevron="true"
      :menu-props="menuProps"
      :disabled="isRestDisabled"
      :menu-key-of-selected-item="menuKeyOfSelectedItem"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    menuList?: Bemob.ListItem[];
    menuKeyOfSelectedItem?: string;
    mainButtonClick?: () => unknown;
    ignoreFirst?: boolean;
    planLimits?: keyof BillingLimits;
    displayText?: boolean;
    ignoreRest?: boolean;
    buttonStyle?: string;
    outline?: string;
    size?: null | 'mini' | 'micro' | 'large';
    icon?: string;
    menuProps?: Record<string, unknown>;
  }>(),
  {
    displayText: true,
    buttonStyle: 'default',
  },
);

const stylesWithBorder = ['accent', 'red'];
const outlineStyles = ['outline-accent', 'outline-alt'];

const firstItem = computed(() =>
  props.menuList && props.menuList.length ? props.menuList[0] : null,
);

const restItems = computed(() => {
  if (!props.menuList || !props.menuList.length) {
    return null;
  }
  return props.ignoreFirst ? props.menuList.slice(1) : props.menuList;
});

const isRestDisabled = computed(() =>
  restItems.value ? restItems.value.every((item) => item.disabled) : true,
);

const hasBorder = computed(() => stylesWithBorder.includes(props.buttonStyle));

const isOutline = computed(() => outlineStyles.includes(props.buttonStyle));

const mainButtonClickHandle = () => {
  if (props.mainButtonClick) {
    return props.mainButtonClick();
  }
  firstItem.value?.click?.();
};
</script>

<style lang="scss">
.ui-button-multi {
  display: flex;
  margin: 0 8px;

  &__first-item {
    margin: 0;

    &--has-rest {
      .ui-button {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }
    }
  }

  &__rest-items {
    margin-left: 0 !important;

    .ui-button {
      border-bottom-left-radius: 0 !important;
      border-top-left-radius: 0 !important;
    }

    min-width: 24px;

    &--has-border .ui-button.accent,
    &--has-border .ui-button.red {
      border-left: solid 1px var(--color-gray40) !important;
    }

    &--no-border {
      .ui-button,
      .ui-button:hover,
      .ui-button:active,
      .ui-button:focus {
        border-left: none !important;
      }
    }
  }
}
</style>
